// @ts-check
/* assets/news-ai.js — GLG-ARTICLE-REVIEWER-01
 * Shopify browser client only.
 * No model key. No local/fallback scoring. No decision authority.
 * Aligned with 3-tier Meta Layout DOM architecture.
 */

(function (/** @type {import('../types/news-ai').NewsWindow} */ window) {
  'use strict';

  var ROOT_SELECTOR = '[data-glg-article-reviewer]';
  const RUNTIME_KEY = '__NEWS_AI_RUNTIME__';
  if (window[RUNTIME_KEY]) { window[RUNTIME_KEY].boot(document); return; }
  /** @type {Map<Element, {controller: AbortController, dispose: (() => void) | null}>} */
  var mounted = new Map();

  /**
   * @template {string} K
   * @param {Element | null} root
   * @param {EventTarget | null} target
   * @param {K} name
   * @param {(event: K extends keyof HTMLElementEventMap ? HTMLElementEventMap[K] : Event) => void} handler
   */
  function listen(root, target, name, handler) {
    var entry = root && mounted.get(root);
    if (target && entry) target.addEventListener(name, /** @type {EventListener} */ (handler), { signal: entry.controller.signal });
  }
  var MIN_ARTICLE_CHARS = 80;

  var CHECK_LABELS = [
    ['受眾', '具體讀者角色'],
    ['傳播動機', '分享理由是否具體'],
    ['轉發風險', '轉發是否安全'],
    ['資訊價值', '清晰判斷／分析框架'],
    ['工作適用性', '會議／簡報／工作群'],
    ['專業證據', '經驗／案例／方法／數據']
  ];

  /** @template {string} S @param {ParentNode} root @param {S} selector @returns {import('../types/news-ai').QueryResult<S> | null} */
  function q(root, selector) {
    return /** @type {import('../types/news-ai').QueryResult<S> | null} */ (root.querySelector(selector));
  }

  /** @template {string} S @param {ParentNode} root @param {S} selector @returns {import('../types/news-ai').QueryResult<S>[]} */
  function qa(root, selector) {
    return /** @type {import('../types/news-ai').QueryResult<S>[]} */ (Array.from(root.querySelectorAll(selector)));
  }


  /**
   * @param {unknown} value
   */
  function text(value) {
    return String(value == null ? '' : value);
  }


  /**
   * @param {unknown} value
   */
  function escapeHtml(value) {
    return text(value).replace(/[&<>"']/g, function (char) {
      return {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
      }[char] || char;
    });
  }


  /**
   * @param {Pick<Response, "json">} response
   */
  function safeJson(response) {
    return response.json().catch(function (error) {
      if (error && (error.code === 'stale_session' || error.name === 'AbortError')) throw error;
      return {};
    });
  }


  /**
   * @param {string} value
   */
  function copyToClipboard(value) {
    if (!navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') {
      return Promise.reject(new Error('clipboard_unavailable'));
    }
    return navigator.clipboard.writeText(value);
  }


  // News AI RTE: the plain-text textarea remains the review/draft authority.

  /**
   * @param {string} value
   */
  function approvedEditorialLink(value) {
    if (!/^https?:\/\//i.test(value)) return false;
    try {
      var url = new URL(value);
      return (url.protocol === 'https:' || url.protocol === 'http:') &&
        !!url.hostname && !url.username && !url.password;
    } catch (_) { return false; }
  }


  /**
   * @param {HTMLElement} editor
   */
  function sanitizedEditorialMarkup(editor) {
    var container = document.createElement('div');
    /** @type {Record<string, boolean>} */
    var allowed = { b: true, strong: true, i: true, em: true, u: true,
      p: true, div: true, br: true, ul: true, ol: true, li: true, a: true };
    /** @type {Record<string, boolean>} */
    var discarded = { script: true, style: true, svg: true, math: true,
      iframe: true, object: true, embed: true, img: true, form: true,
      input: true, video: true, audio: true };

    /**
     * @param {Node} node
     * @param {Node} target
     */
    function copySafe(node, target) {
      if (node.nodeType === 3) {
        target.appendChild(document.createTextNode(node.nodeValue || ''));
        return;
      }
      if (node.nodeType !== 1) return;
      var elementNode = /** @type {Element} */ (node);
      var tag = elementNode.tagName.toLowerCase();
      if (discarded[tag]) return;
      var href = tag === 'a' ? elementNode.getAttribute('href') || '' : '';
      var safeTag = tag === 'a' && !approvedEditorialLink(href) ? 'span' :
        allowed[tag] ? tag : 'span';
      var element = document.createElement(safeTag);
      if (safeTag === 'a') {
        element.setAttribute('href', href);
        element.setAttribute('rel', 'noopener noreferrer');
      }
      Array.prototype.forEach.call(node.childNodes, function (child) {
        copySafe(child, element);
      });
      target.appendChild(element);
    }
    Array.prototype.forEach.call(editor.childNodes, function (node) {
      copySafe(node, container);
    });
    return container.innerHTML;
  }


  /**
   * @param {HTMLElement | null} container
   * @param {HTMLTextAreaElement | null} article
   * @param {(value: string) => void} onChange
   */
  function initRichTextEditor(container, article, onChange) {
    if (!container || !article) return function () {};
    const maybeEditor = q(container, '[data-rte-content]');
    if (!maybeEditor) return function () {};
    const editor = maybeEditor;
    var root = container.closest(ROOT_SELECTOR);
    var undo = q(container, '[data-rte-undo]');
    var redo = q(container, '[data-rte-redo]');
    /** @type {string[]} */
    var history = [];
    var index = 0;
    function buttons() {
      if (undo) undo.disabled = index === 0;
      if (redo) redo.disabled = index === history.length - 1;
    }
    function notify() {
      onChange(editor.innerText || editor.textContent || '');
    }
    function record() {
      var snapshot = sanitizedEditorialMarkup(editor);
      if (snapshot !== editor.innerHTML) {
        editor.innerHTML = snapshot;
        editor.focus();
      }
      if (snapshot !== history[index]) {
        history = history.slice(0, index + 1);
        history.push(snapshot);
        if (history.length > 51) history.shift();
        index = history.length - 1;
      }
      buttons();
      notify();
    }

    /**
     * @param {number} position
     */
    function restore(position) {
      if (position < 0 || position >= history.length) return;
      index = position;
      editor.innerHTML = history[index]; // Allowlist-only snapshots.
      buttons();
      notify();
      editor.focus();
    }

    /**
     * @param {string} value
     */
    function insertText(value) {
      editor.focus();
      if (!document.execCommand('insertText', false, value)) {
        var selection = window.getSelection();
        if (!selection || !selection.rangeCount ||
            !editor.contains(selection.anchorNode)) {
          editor.appendChild(document.createTextNode(value));
        } else {
          var range = selection.getRangeAt(0);
          range.deleteContents();
          var node = document.createTextNode(value);
          range.insertNode(node);
          range.setStartAfter(node);
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }
      record();
    }

    /**
     * @param {string} name
     * @param {string} [value]
     */
    function command(name, value) {
      editor.focus();
      var selection = window.getSelection();
      if (!selection || !selection.rangeCount || !editor.contains(selection.anchorNode) || !editor.contains(selection.focusNode)) return;
      document.execCommand(name, false, value || undefined);
      record();
    }
    qa(container, '[data-rte-cmd]').forEach(function (button) {
      const name = button.getAttribute('data-rte-cmd');
      if (!name || ['bold', 'italic', 'underline', 'insertUnorderedList', 'insertOrderedList'].indexOf(name) < 0) return;
      listen(root, button, 'mousedown', function (event) { event.preventDefault(); });
      listen(root, button, 'click', function () { command(name); });
    });
    var link = q(container, '[data-rte-link]');
    if (link) {
      listen(root, link, 'mousedown', function (event) { event.preventDefault(); });
      listen(root, link, 'click', function () {
        var value = window.prompt('連結網址（HTTPS/HTTP）：', 'https://');
        if (!value) return;
        value = value.trim();
        if (!approvedEditorialLink(value)) { window.alert('只接受有效的 HTTPS 或 HTTP 連結。'); return; }
        command('createLink', value);
      });
    }
    if (undo) listen(root, undo, 'click', function () { restore(index - 1); });
    if (redo) listen(root, redo, 'click', function () { restore(index + 1); });
    listen(root, editor, 'keydown', function (event) {
      if (!(event.metaKey || event.ctrlKey) || event.altKey) return;
      var key = event.key.toLowerCase();
      if (key === 'z' || key === 'y') {
        event.preventDefault();
        restore(key === 'y' || event.shiftKey ? index + 1 : index - 1);
      }
    });
    listen(root, editor, 'beforeinput', function (event) {
      if (event.inputType === 'historyUndo' || event.inputType === 'historyRedo') {
        event.preventDefault();
        restore(index + (event.inputType === 'historyUndo' ? -1 : 1));
      }
    });
    listen(root, editor, 'paste', function (event) {
      event.preventDefault();
      insertText(event.clipboardData ? event.clipboardData.getData('text/plain') : '');
    });
    listen(root, editor, 'drop', function (event) {
      event.preventDefault(); // Never insert HTML or executable file previews.
      var dropped = event.dataTransfer && event.dataTransfer.getData('text/plain');
      if (dropped) insertText(dropped);
    });
    listen(root, editor, 'input', record);
    const syncFromArticle = function syncFromArticle() {
      editor.textContent = article.value;
      history = [sanitizedEditorialMarkup(editor)];
      index = 0;
      buttons();
    };
    syncFromArticle();
    return syncFromArticle;
  }



  /**
   * @param {HTMLElement} container
   */
  function initFileUploader(container) {
    const maybeInput = q(container, '[data-uploader-input]');
    const maybeDropzone = q(container, '[data-uploader-dropzone]');
    const maybeList = q(container, '[data-uploader-list]');
    if (!maybeInput || !maybeDropzone || !maybeList) return function () {};
    const input = maybeInput;
    const dropzone = maybeDropzone;
    const list = maybeList;
    var root = container.closest(ROOT_SELECTOR);
    /** @type {Map<string, {node: HTMLLIElement, progress: number, timer: number | undefined}>} */
    var files = new Map();
    var sequence = 0;

    /**
     * @param {number} size
     */
    function formatSize(size) {
      return size < 1024 ? size + ' B' : size < 1048576
        ? (size / 1024).toFixed(1) + ' KB' : (size / 1048576).toFixed(1) + ' MB';
    }

    /**
     * @param {FileList | null} incoming
     */
    function addFiles(incoming) {
      Array.prototype.forEach.call(incoming, function (file) {
        var id = String(++sequence);
        var item = document.createElement('li');
        item.className = 'news-ai-file';
        var name = document.createElement('strong'); name.textContent = file.name;
        var size = document.createElement('span'); size.textContent = formatSize(file.size);
        var meter = document.createElement('progress'); meter.max = 100; meter.value = 0;
        meter.setAttribute('aria-label', file.name + ' · 模擬進度');
        var status = document.createElement('span'); status.textContent = '模擬進度 0% · 未上傳';
        status.setAttribute('data-file-status', '');
        var remove = document.createElement('button'); remove.type = 'button';
        remove.dataset.fileRemove = id; remove.textContent = '移除';
        remove.setAttribute('aria-label', '移除 ' + file.name);
        item.append(name, size, meter, status, remove);
        list.appendChild(item);
        /** @type {{node: HTMLLIElement, progress: number, timer: number | undefined}} */
        var record = { node: item, progress: 0, timer: undefined };
        files.set(id, record);
        record.timer = window.setInterval(function () {
          record.progress = Math.min(100, record.progress + 20);
          meter.value = record.progress;
          status.textContent = record.progress === 100 ? '模擬完成 100% · 未上傳' : '模擬進度 ' + record.progress + '% · 未上傳';
          if (record.progress === 100) window.clearInterval(record.timer);
        }, 100);
      });
    }
    listen(root, dropzone, 'click', function () { input.click(); });
    listen(root, input, 'change', function () { addFiles(input.files); input.value = ''; });
    ['dragenter', 'dragover'].forEach(function (name) {
      listen(root, dropzone, name, function (event) { event.preventDefault(); dropzone.classList.add('is-dragover'); });
    });
    listen(root, dropzone, 'dragleave', function (event) {
      if (!dropzone.contains(event.relatedTarget instanceof Node ? event.relatedTarget : null)) dropzone.classList.remove('is-dragover');
    });
    listen(root, dropzone, 'drop', function (event) {
      event.preventDefault();
      dropzone.classList.remove('is-dragover');
      if (event.dataTransfer) addFiles(event.dataTransfer.files);
    });
    listen(root, list, 'click', function (event) {
      var button = event.target instanceof Element ? event.target.closest('[data-file-remove]') : null;
      var id = button?.getAttribute('data-file-remove');
      var file = id ? files.get(id) : undefined;
      if (!file) return;
      window.clearInterval(file.timer);
      file.node.remove();
      if (id) files.delete(id);
      dropzone.focus();
    });
    return function clearAttachments() {
      files.forEach(function (file) { window.clearInterval(file.timer); });
      files.clear(); list.replaceChildren(); input.value = '';
      dropzone.classList.remove('is-dragover');
    };
  }

  // Independent Markdown scratchpad: never submit its contents to Reviewer silently.
  // Preview uses safe DOM text nodes, not user-controlled HTML.

  /**
   * @param {HTMLElement} container
   */
  function initMarkdownEditor(container) {
    if (!container || container.dataset.mdInitialized === 'true') return function () {};
    const maybeInput = q(container, '[data-md-input]');
    const maybePreview = q(container, '[data-md-preview]');
    const maybePane = q(container, '[data-md-pane]');
    if (!maybeInput || !maybePreview || !maybePane) return function () {};
    const input = maybeInput;
    const preview = maybePreview;
    const pane = maybePane;
    container.dataset.mdInitialized = 'true';
    var root = container.closest(ROOT_SELECTOR);
    var tabs = qa(container, '[data-md-tab]');

    /**
     * @param {Node} parent
     * @param {string} value
     */
    function appendInline(parent, value) {
      var tokens = String(value).split(/(\*\*[^*]+\*\*|\x60[^\x60]+\x60|\*[^*]+\*)/g);
      tokens.forEach(function (token) {
        var type = '';
        var content = token;
        if (/^\*\*[^*]+\*\*$/.test(token)) {
          type = 'strong'; content = token.slice(2, -2);
        } else if (/^\x60[^\x60]+\x60$/.test(token)) {
          type = 'code'; content = token.slice(1, -1);
        } else if (/^\*[^*]+\*$/.test(token)) {
          type = 'em'; content = token.slice(1, -1);
        }
        var node = type ? document.createElement(type) : document.createTextNode(content);
        if (type) node.textContent = content;
        parent.appendChild(node);
      });
    }
    function renderMarkdown() {
      var fragment = document.createDocumentFragment();
      var lines = input.value.slice(0, 80000).split(/\r?\n/).slice(0, 2000);
      /** @type {HTMLUListElement | null} */
      var list = null;
      /** @type {HTMLElement | null} */
      var code = null;
      lines.forEach(function (line) {
        if (/^\x60{3}/.test(line)) {
          if (code) code = null;
          else {
            var pre = document.createElement('pre');
            code = document.createElement('code');
            pre.appendChild(code);
            fragment.appendChild(pre);
          }
          list = null;
          return;
        }
        if (code) {
          code.appendChild(document.createTextNode(line + '\n'));
          return;
        }
        var heading = /^(#{1,3})\s+(.*)$/.exec(line);
        var bullet = /^\s*-\s+(.+)$/.exec(line);
        if (heading) {
          list = null;
          var h = document.createElement('h' + heading[1].length);
          appendInline(h, heading[2]);
          fragment.appendChild(h);
        } else if (bullet) {
          if (!list) { list = document.createElement('ul'); fragment.appendChild(list); }
          var item = document.createElement('li');
          appendInline(item, bullet[1]);
          list.appendChild(item);
        } else if (/^>\s+/.test(line)) {
          list = null;
          var quote = document.createElement('blockquote');
          appendInline(quote, line.replace(/^>\s+/, ''));
          fragment.appendChild(quote);
        } else if (line.trim()) {
          list = null;
          var p = document.createElement('p');
          appendInline(p, line);
          fragment.appendChild(p);
        } else list = null;
      });
      preview.replaceChildren(fragment);
      syncScroll();
    }
    function syncScroll() {
      var range = input.scrollHeight - input.clientHeight;
      preview.scrollTop = range > 0 ? (input.scrollTop / range) * (preview.scrollHeight - preview.clientHeight) : 0;
    }

    /**
     * @param {string | null} name
     */
    function setMode(name) {
      if (!name || ['split', 'edit', 'preview'].indexOf(name) < 0) return;
      pane.dataset.mdPane = name;
      tabs.forEach(function (tab) {
        var active = tab.getAttribute('data-md-tab') === name;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.setAttribute('tabindex', active ? '0' : '-1');
      });
      syncScroll();
    }
    tabs.forEach(function (tab) {
      listen(root, tab, 'click', function () { setMode(tab.getAttribute('data-md-tab')); });
    });
    listen(root, input, 'input', renderMarkdown);
    listen(root, input, 'scroll', syncScroll);
    tabs.forEach(function (tab, index) {
      listen(root, tab, 'keydown', function (event) {
        var next = event.key === 'ArrowRight' ? (index + 1) % tabs.length
          : event.key === 'ArrowLeft' ? (index + tabs.length - 1) % tabs.length
          : event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : -1;
        if (next < 0) return;
        event.preventDefault();
        setMode(tabs[next].getAttribute('data-md-tab'));
        tabs[next].focus();
      });
    });
    setMode('split');
    renderMarkdown();
    return function clearMarkdownForIdentityChange() {
      input.value = '';
      renderMarkdown();
      setMode('split');
    };
  }


/**
 * @param {HTMLElement} root
 */
function init(root) {
    if (!root || root.dataset.reviewerInitialized === 'true') return;
    root.dataset.reviewerInitialized = 'true';
    // Opt-in Coming Soon preview is inert: never start protected/public API operations.
    if (root.classList.contains('p5-coming-soon-host')) return;
    mounted.set(root, { controller: new AbortController(), dispose: null });

    var reviewEndpoint = (root.dataset.reviewEndpoint || '').trim();
    var healthEndpoint = (root.dataset.healthEndpoint || '').trim();
    var publicSearchEndpoint = (root.dataset.publicSearchEndpoint || '').trim();
    /** @type {AbortController | null} */
    var publicSearchController = null;
    function publicSearchApproved() {
      return publicSearchEndpoint === 'https://api.glhksolution.com/v1/content/news-search' &&
        root.dataset.publicNewsSource === 'google-news-rss-v1' && root.dataset.publicNewsLocale === 'zh-HK';
    }
    var draftEndpoint = (root.dataset.draftEndpoint || '').trim();

    var els = {
      writerTopic: q(root, '[data-writer-topic]'),
      writerSources: q(root, '[data-writer-sources]'),
      writerEvidence: q(root, '[data-writer-evidence]'),
      writerSearch: q(root, '[data-writer-search]'),
      writerGenerate: q(root, '[data-writer-generate]'),
      writerResults: q(root, '[data-writer-results]'),
      writerState: q(root, '[data-writer-state]'),
      writerProof: q(root, '[data-writer-proof]'),
      title: q(root, '[data-reviewer-title]'),
      article: q(root, '[data-reviewer-article]'),
      audience: q(root, '[data-reviewer-audience]'),
      evidence: q(root, '[data-reviewer-evidence]'),
      channel: q(root, '[data-reviewer-channel]'),
      runButton: q(root, '[data-reviewer-run]'),
      exampleButton: q(root, '[data-reviewer-example]'),
      copyAllButton: q(root, '[data-reviewer-copy-all]'),
      wordCount: q(root, '[data-reviewer-word-count]'),

      placeholder: q(root, '[data-reviewer-placeholder]'),
      loading: q(root, '[data-reviewer-loading]'),
      error: q(root, '[data-reviewer-error]'),
      errorMessage: q(root, '[data-reviewer-error-message]'),
      errorRun: q(root, '[data-reviewer-error-run]'),

      summary: q(root, '[data-reviewer-summary]'),
      decision: q(root, '[data-reviewer-decision]'),
      decisionTitle: q(root, '[data-reviewer-decision-title]'),
      score: q(root, '[data-reviewer-score]'),
      summaryCopy: q(root, '[data-reviewer-summary-copy]'),
      runId: q(root, '[data-reviewer-run-id]'),
      interactionId: q(root, '[data-reviewer-interaction-id]'),
      usage: q(root, '[data-reviewer-usage]'),

      checks: q(root, '[data-reviewer-checks]'),
      outputs: q(root, '[data-reviewer-outputs]'),
      shareLine: q(root, '[data-reviewer-share-line]'),
      meetingLine: q(root, '[data-reviewer-meeting-line]'),
      priorityFix: q(root, '[data-reviewer-priority-fix]'),
      authority: q(root, '[data-reviewer-authority]'),
      missing: q(root, '[data-reviewer-missing]'),
      toast: q(root, '[data-reviewer-toast]')
    };

    var syncRteFromArticle = initRichTextEditor(
      q(root, '[data-feature="rte"]'), els.article, function (value) {
        if (!els.article) return;
        var changed = els.article.value !== value;
        els.article.value = value;
        updateWordCount();
        if (changed) { cancelWriter(); invalidateReview(); }
      }
    );

    var clearMarkdownScratchpads = qa(root, '[data-feature="md-editor"]').map(initMarkdownEditor);
    var clearAttachments = qa(root, '[data-feature="file-uploader"]').map(initFileUploader);
    qa(root, 'details').forEach(function (details) {
      var summary = q(details, 'summary');
      function syncExpanded() { if (summary) summary.setAttribute('aria-expanded', details.open ? 'true' : 'false'); }
      listen(root, details, 'toggle', syncExpanded);
      syncExpanded();
    });

    // Canonical public modes: Discover (default) and Studio.
    // Legacy hashes remain aliases; they never grant API permissions.
    var workspaces = ['discover', 'studio'];

    /**
     * @param {string} hash
     */
    function normalizeWorkspace(hash) {
      if (hash === 'studio' || hash === 'writer' || hash === 'reviewer') return 'studio';
      return 'discover';
    }
    function syncWorkspace() {
      var name = normalizeWorkspace(window.location.hash.replace(/^#/, ''));
      root.dataset.newsAiWorkspace = name;
      var panel = q(root, '[data-news-ai-result-panel]');
      root.dataset.newsAiHasResult = panel && !panel.hidden ? 'true' : 'false';
      const editor = q(root, '[data-news-ai-review-form]');
      var writer = q(root, '[data-reviewer-writer]');
      var research = q(root, '[data-news-ai-research-block]');
      if (research) { research.hidden = name !== 'discover'; research.inert = name !== 'discover'; }
      var layout = q(root, '.glg-article-reviewer__layout');
      if (layout) { layout.hidden = name !== 'studio'; layout.inert = name !== 'studio'; }
      [editor, writer].forEach(function (node) {
        if (!node) return;
        node.hidden = name !== 'studio';
        node.inert = name !== 'studio';
      });
      qa(root, '[data-news-ai-tab]').forEach(function (tab) {
        var active = tab.dataset.newsAiTab === name;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.setAttribute('tabindex', active ? '0' : '-1');
        if (active) tab.setAttribute('aria-current', 'page');
        else tab.removeAttribute('aria-current');
      });
      // Mirror the two canonical modes in the shared Sidebar; legacy hashes remain aliases.
      qa(document, '[data-news-ai-sidebar-tab]').forEach(function (link) {
        var active = link.dataset.newsAiSidebarTab === name;
        link.classList.toggle('is-active', active);
        if (active) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
      qa(root, '[data-news-ai-panel]').forEach(function (panel) {
        panel.hidden = true;
      });
      if (document.activeElement && root.contains(document.activeElement) &&
          (document.activeElement.closest('[hidden]') || document.activeElement.closest('[inert]'))) {
        var target = q(root, '[data-news-ai-tab][aria-current="page"]');
        if (target) target.focus();
      }
    }
    var modeTabs = qa(root, '[data-news-ai-tab]');
    modeTabs.forEach(function (tab, index) {
      listen(root, tab, 'click', function () {
        var desired = tab.dataset.newsAiTab || '';
        if (workspaces.indexOf(desired) < 0) return;
        if (window.location.hash !== '#' + desired) window.location.hash = desired;
        else syncWorkspace();
      });
      listen(root, tab, 'keydown', function (event) {
        var next = event.key === 'ArrowRight' ? (index + 1) % modeTabs.length
          : event.key === 'ArrowLeft' ? (index + modeTabs.length - 1) % modeTabs.length
          : event.key === 'Home' ? 0 : event.key === 'End' ? modeTabs.length - 1 : -1;
        if (next < 0) return;
        event.preventDefault(); modeTabs[next].click(); modeTabs[next].focus();
      });
    });
    listen(root, window, 'hashchange', syncWorkspace);
    syncWorkspace();

    // Explicit manual, tab-scoped, non-credential editor draft storage.
    var DRAFT_KEY = 'p5-news-ai-manual-article-v1';
    function draftOwner() {
      const session = window.LittleGWebSession;
      var state = session && session.getState && session.getState();
      return state && state.status === 'authenticated' && state.profile && state.profile.id
        ? 'account:' + String(state.profile.id) : 'guest';
    }
    var draftStatus = q(root, '[data-news-ai-draft-status]');

    /**
     * @param {string} message
     */
    function draftMessage(message) { if (draftStatus) draftStatus.textContent = message; }
    function draftStorage() {
      try { return window.sessionStorage || null; } catch (_) { return null; }
    }
    function clearManualDraft() {
      var storage = draftStorage();
      if (!storage) return;
      try { storage.removeItem(DRAFT_KEY); } catch (_) {}
    }


   function saveManualDraft() {
      var storage = draftStorage();
      if (!storage) { draftMessage('此瀏覽器無法暫存；請先自行複製文章。'); return; }
      try {
        storage.setItem(DRAFT_KEY, JSON.stringify({
          owner: draftOwner(),
          title: els.title ? els.title.value.slice(0, 300) : '',
          article: els.article ? els.article.value.slice(0, 30000) : ''
        }));
        draftMessage('草稿已暫存於此分頁；不包含身份憑證或新聞來源。');
      } catch (_) { draftMessage('暫存失敗；請先自行複製文章。'); }
    }
    function restoreManualDraft() {
      var storage = draftStorage();
      if (!storage) { draftMessage('此瀏覽器無法恢復草稿。'); return; }
      try {
        var raw = storage.getItem(DRAFT_KEY);
        if (!raw) { draftMessage('此分頁沒有已暫存草稿。'); return; }
        var saved = JSON.parse(raw);
        if (!saved || saved.owner !== draftOwner() || typeof saved.title !== 'string' || typeof saved.article !== 'string') throw Error('invalid_draft');
        if (els.title) els.title.value = saved.title.slice(0, 300);
        if (els.article) els.article.value = saved.article.slice(0, 30000);
        syncRteFromArticle();
        updateWordCount();
        resetResult();
        draftMessage('已恢復本機草稿；請核實內容後再使用 AI。');
      } catch (_) { clearManualDraft(); draftMessage('草稿已失效，無法恢復。'); }
    }
    var saveDraftButton = q(root, '[data-news-ai-draft-save]');
    var restoreDraftButton = q(root, '[data-news-ai-draft-restore]');
    var clearDraftButton = q(root, '[data-news-ai-draft-clear]');
    if (saveDraftButton) listen(root, saveDraftButton, 'click', saveManualDraft);
    if (restoreDraftButton) listen(root, restoreDraftButton, 'click', restoreManualDraft);
    if (clearDraftButton) listen(root, clearDraftButton, 'click', function () {
      clearManualDraft();
      draftMessage('本機草稿已清除。');
    });
    var sessionEpoch = 0;
    var searchSequence = 0;
    var writerSequence = 0;
    var reviewSequence = 0;
    var healthSequence = 0;
    /** @type {AbortController | null} */
    var healthController = null;
    /** @type {string | null} */
    var reviewedArticle = null;
    var editorOwner = draftOwner();
    /** @type {AbortController[]} */
    var pendingRequests = [];
    function stopPendingRequests() {
      if (healthController) healthController.abort();
      healthController = null;
      if (writerController) writerController.abort();
      writerController = null;
      if (publicSearchController) publicSearchController.abort();
      publicSearchController = null;
      pendingRequests.forEach(function (controller) { try { controller.abort(); } catch (_) {} });
      pendingRequests.length = 0;
    }
    /** @type {AbortController | null} */
    var reviewController = null;
    /** @type {AbortController | null} */
    var writerController = null;
    var runtimeReady = false;
    var researchReady = false;
    /** @type {import('../types/news-ai').ReviewResult | null} */
    var latestResult = null;
    /** @type {number | undefined} */
    var toastTimer = undefined;


 /**
  * @param {string} url
  * @param {RequestInit} [options]
  */
 function nativeRequest(url, options) {
      const candidate = window.LittleGWebSession;
      if (!candidate || typeof candidate.authenticatedFetch !== 'function') {
        return Promise.reject(new Error('需要有效登入；此功能不支援舊版 App Proxy 身份。'));
      }
      const session = candidate;
      var epoch = sessionEpoch;
      var state = typeof session.getState === 'function' ? session.getState() : null;
      var subject = state && state.profile && state.profile.id;
      var controller = typeof AbortController === 'function' ? new AbortController() : null;
      var requestOptions = Object.assign({}, options || {});
      if (controller && !requestOptions.signal) {
        requestOptions.signal = controller.signal;
        pendingRequests.push(controller);
      }
      function current() {
        var next = typeof session.getState === 'function' ? session.getState() : null;
        if (!root.isConnected || sessionEpoch !== epoch || session !== window.LittleGWebSession ||
            (subject && (!next || next.status !== 'authenticated' || !next.profile || next.profile.id !== subject))) {
          /** @type {import('../types/news-ai').RuntimeError} */
          var error = new Error('Session changed; stale AI response ignored');
          error.code = 'stale_session';
          throw error;
        }
      }
      return session.authenticatedFetch(url, requestOptions).then(function (response) {
        current();
        return { ok: response.ok, status: response.status, json: function () {
          return response.json().then(function (data) { current(); return data; });
        } };
      }).finally(function () {
        if (!controller) return;
        var index = pendingRequests.indexOf(controller);
        if (index >= 0) pendingRequests.splice(index, 1);
      });
    }


    /**
     * @param {string} value
     */
    function writerState(value) {
      if (els.writerState) els.writerState.textContent = value;
    }


    /**
     * @param {string} message
     */
    function showToast(message) {
      if (!els.toast) return;
      els.toast.textContent = message;
      els.toast.dataset.visible = 'true';
      window.clearTimeout(toastTimer);
      toastTimer = window.setTimeout(function () {
        if (els.toast) els.toast.dataset.visible = 'false';
      }, 1400);
    }

  function writerReady() { return runtimeReady && root.dataset.writerApproved === 'true'; }
    function reviewerReady() { return runtimeReady && root.dataset.reviewerApproved === 'true'; }


    /**
     * @param {string} state
     */
    function setRuntime(state) {
      runtimeReady = state === 'live';

      researchReady = root.dataset.publicSearchApproved === 'true' &&
        root.dataset.publicNewsCacheSeconds === '900' &&
        root.dataset.publicNewsStaleFallback === 'false' &&
        publicSearchApproved();

      root.dataset.newsAiRuntime = state;

      if (els.writerSearch) els.writerSearch.disabled = !researchReady;
      if (els.writerGenerate) els.writerGenerate.disabled = !writerReady();
      if (els.runButton) {
        els.runButton.disabled = !reviewerReady();
        els.runButton.textContent = reviewerReady() ? '執行 AI 文章審查' : 'AI 審查尚未開放或服務暫不可用';
      }
    }

   function resetResult() {
      latestResult = null;
      reviewedArticle = null;
      var resultPanel = q(root, '[data-news-ai-result-panel]');
      if (resultPanel) resultPanel.hidden = true;
      root.dataset.newsAiHasResult = 'false';
      if (els.placeholder) els.placeholder.hidden = true;
      if (els.loading) els.loading.hidden = true;
      if (els.error) els.error.hidden = true;
      if (els.summary) els.summary.hidden = true;
      if (els.outputs) els.outputs.hidden = true;
      if (els.checks) els.checks.innerHTML = '';
    }


    /**
     * @param {string} message
     * @param {string | null} [runId]
     */
    function showError(message, runId) {
      resetResult();
      if (els.placeholder) els.placeholder.hidden = true;
      if (els.errorMessage) els.errorMessage.textContent = message || '未知 AI 服務錯誤';
      if (els.errorRun) els.errorRun.textContent = runId ? 'run_id: ' + runId : '';
      if (els.error) els.error.hidden = false;
      var resultPanel = q(root, '[data-news-ai-result-panel]');
      if (resultPanel) resultPanel.hidden = false;
      root.dataset.newsAiHasResult = 'true';
    }

    function updateWordCount() {
      if (!els.article || !els.wordCount) return;
      els.wordCount.textContent = Array.from(els.article.value.replace(/\s/g, '')).length + ' 字';
    }

    function invalidateReview() {
      if (reviewController) {
        reviewSequence++;
        reviewController.abort();
        reviewController = null;
        if (els.runButton) {
          els.runButton.disabled = !reviewerReady();
          els.runButton.textContent = reviewerReady() ? '執行 AI 文章審查' : 'AI 審查尚未開放或服務暫不可用';
        }
      }
      resetResult();
    }

    function cancelWriter() {
      if (!writerController) return;
      writerSequence++;
      writerController.abort();
      writerController = null;
      if (els.writerGenerate) els.writerGenerate.disabled = !writerReady();
      writerState('原稿或來源已變更，本次草稿請求已取消；如後端已開始執行，可能仍會計入額度。');
    }


    /**
     * @param {import('../types/news-ai').ReviewCheck} check
     */
    function locateEvidence(check) {
      if (!els.article || reviewedArticle === null || els.article.value !== reviewedArticle) return null;
      var quote = check.evidence_quote;
      var span = check.evidence_span;
      var article = reviewedArticle.trim();
      if (typeof quote !== 'string' || !quote.length || !span ||
          !Number.isInteger(span.start) || !Number.isInteger(span.end) ||
          span.start < 0 || span.end <= span.start || span.end > article.length ||
          article.slice(span.start, span.end) !== quote ||
          article.indexOf(quote) !== span.start || article.indexOf(quote, span.start + 1) !== -1) return null;
      var leading = reviewedArticle.length - reviewedArticle.trimStart().length;
      return { start: span.start + leading, end: span.end + leading };
    }


    /**
     * @param {import('../types/news-ai').ReviewCheck} check
     * @param {number} index
     */
    function renderCheck(check, index) {
      var id = Number(check.id || index + 1);
      var label = CHECK_LABELS[id - 1] || ['檢查 ' + id, ''];
      var status = ['PASS', 'REVISE', 'BLOCK'].indexOf(check.status) >= 0 ? check.status : 'REVISE';
      var symbol = status === 'PASS' ? '✓' : status === 'BLOCK' ? '×' : '!';

      return [
        '<article class="glg-article-reviewer__check">',
          '<div class="glg-article-reviewer__check-top">',
            '<span class="glg-article-reviewer__status" data-status="', escapeHtml(status), '">', symbol, '</span>',
            '<div class="glg-article-reviewer__check-title">',
              '<strong>', String(id).padStart(2, '0'), ' · ', escapeHtml(label[0]), '</strong>',
              '<span>', escapeHtml(label[1]), '</span>',
            '</div>',
            '<span class="glg-article-reviewer__check-state">', escapeHtml(status === 'PASS' ? '優秀' : status === 'BLOCK' ? '不合格' : '普通'), '</span>',
          '</div>',
          '<div class="glg-article-reviewer__check-body">',
            '<p>', escapeHtml(check.reason || ''), '</p>',
            check.evidence_quote
              ? '<div class="glg-article-reviewer__quote">正文證據：「' + escapeHtml(check.evidence_quote) + '」' +
                (locateEvidence(check)
                  ? ' <button type="button" data-reviewer-locate="' + String(id) + '">定位正文</button>'
                  : ' <span>無法唯一定位原文</span>') + '</div>'
              : '',
            '<div class="glg-article-reviewer__fix"><strong>最小修訂：</strong> ',
              escapeHtml(check.fix || '無需修改'),
            '</div>',
          '</div>',
        '</article>'
      ].join('');
    }


    /**
     * @param {Partial<import('../types/news-ai').ReviewResult> | null | undefined} data
     * @returns {data is import('../types/news-ai').ReviewResult}
     */
    function validVerifiedResult(data) {
      return Boolean(
        data &&
        data.runtime &&
        data.runtime.verified_ai_run === true &&
        typeof data.interaction_id === 'string' && data.interaction_id.trim() &&
        typeof data.run_id === 'string' && data.run_id.trim() &&
        typeof data.score === 'number' && Number.isFinite(data.score) && data.score >= 0 && data.score <= 100 &&
        ['PASS', 'REVISE', 'BLOCK'].indexOf(data.decision || '') >= 0 &&
        Array.isArray(data.checks) &&
        data.checks.length === 6 &&
        new Set(data.checks.map(function (check) { return check && check.id; })).size === 6 &&
        data.checks.every(function (check) {
          return check && Number.isInteger(check.id) && check.id >= 1 && check.id <= 6 &&
            ['PASS', 'REVISE', 'BLOCK'].indexOf(check.status) >= 0 &&
            typeof check.reason === 'string' && typeof check.fix === 'string';
        })
      );
    }


    /**
     * @param {Partial<import('../types/news-ai').ReviewResult> | null} data
     */
    function render(data) {
      if (!validVerifiedResult(data)) {
        showError('審查結果缺少有效 AI 執行證據，因此不顯示評級。', data && data.run_id);
        return;
      }

      latestResult = data;
      var resultPanel = q(root, '[data-news-ai-result-panel]');
      if (resultPanel) resultPanel.hidden = false;
      root.dataset.newsAiHasResult = 'true';

      if (els.placeholder) els.placeholder.hidden = true;
      if (els.loading) els.loading.hidden = true;
      if (els.error) els.error.hidden = true;

      var decision = ['PASS', 'REVISE', 'BLOCK'].indexOf(data.decision || '') >= 0
        ? data.decision
        : 'REVISE';

      if (els.decision) {
        els.decision.textContent = decision === 'PASS' ? '優秀' : decision === 'BLOCK' ? '不合格' : '普通';
        els.decision.dataset.decision = decision;
      }

      if (els.decisionTitle) {
        els.decisionTitle.textContent =
          decision === 'PASS' ? '優秀 · 可進入人工發佈審核' :
          decision === 'BLOCK' ? '不合格 · 暫不可發佈' :
          '普通 · 需要修訂';
      }

      if (els.score) els.score.textContent = String(Number.isFinite(Number(data.score)) ? Number(data.score) : 0);
      if (els.summaryCopy) els.summaryCopy.textContent = data.summary || '';
      if (els.runId) els.runId.textContent = data.run_id;
      if (els.interactionId) els.interactionId.textContent = data.interaction_id;

      var usage = (data.runtime && data.runtime.usage) || {};
      if (els.usage) {
        els.usage.textContent =
          text(data.runtime && data.runtime.latency_ms || '—') +
          ' ms · ' +
          text(usage.total_tokens || '—') +
          ' tokens';
      }

      if (els.checks) {
        els.checks.innerHTML = data.checks.map(renderCheck).join('');
        qa(els.checks, '[data-reviewer-locate]').forEach(function (button) {
          listen(root, button, 'click', function () {
            var check = data.checks.find(function (item) {
              return Number(item.id) === Number(button.getAttribute('data-reviewer-locate'));
            });
            var span = check && locateEvidence(check);
            if (!span || !els.article) return;
            els.article.focus();
            els.article.setSelectionRange(span.start, span.end);
            els.article.scrollIntoView({ block: 'center', behavior: 'auto' });
          });
        });
      }

      if (els.shareLine) els.shareLine.textContent = data.share_line || '—';
      if (els.meetingLine) els.meetingLine.textContent = data.meeting_line || '—';
      if (els.priorityFix) els.priorityFix.textContent = data.priority_fix || '—';
      if (els.authority) {
        els.authority.textContent =
          Array.isArray(data.authority_evidence) && data.authority_evidence.length
            ? data.authority_evidence.join('；')
            : '尚未找到足夠的專業證據';
      }
      if (els.missing) {
        els.missing.textContent =
          Array.isArray(data.missing_evidence) && data.missing_evidence.length
            ? data.missing_evidence.join('；')
            : '無';
      }

      if (els.summary) els.summary.hidden = false;
      if (els.outputs) els.outputs.hidden = false;
    }

  function checkHealth() {
      var epoch = sessionEpoch;
      var sequence = ++healthSequence;
      if (healthController) healthController.abort();
      healthController = typeof AbortController === 'function' ? new AbortController() : null;
      if (!healthEndpoint) {
        setRuntime('offline');
        if (els.placeholder) {
          var statusText = q(els.placeholder, 'p');
          if (statusText) statusText.textContent = 'AI 服務狀態接口未設定，無法產生審查結果。';
        }
        return Promise.resolve();
      }

      setRuntime('checking');

      return nativeRequest(healthEndpoint, {
        signal: healthController ? healthController.signal : undefined,
        method: 'GET',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        credentials: 'same-origin'
      })
        .then(function (response) {
          return safeJson(response).then(function (data) {
            if (epoch !== sessionEpoch || sequence !== healthSequence) return;
            if (!response.ok || !data.ok) {
              throw new Error(data.error || '服務不可用');
            }
            setRuntime(data.configured ? 'live' : 'offline');
            if (data.research_ready && !data.configured) writerState('資料搜尋可用；AI 生成及審核目前停用。');
          });
        })
        .catch(function (error) {
          if (epoch !== sessionEpoch || sequence !== healthSequence || error && (error.code === 'stale_session' || error.name === 'AbortError')) return;
          setRuntime('offline');
          if (els.placeholder) {
            var message = q(els.placeholder, 'p');
            if (message) {
              message.textContent =
                (error && error.message ? error.message : 'AI 服務暫不可用') +
                '。本頁不會使用本機規則假造 AI 結果。';
            }
          }
        });
    }

    function runReview() {
      if (reviewController) {
        reviewSequence++;
        reviewController.abort();
        reviewController = null;
        if (els.loading) els.loading.hidden = true;
        if (els.placeholder) els.placeholder.hidden = false;
        if (els.runButton) els.runButton.textContent = '執行 AI 文章審查';
        showToast('已取消本次審查請求；如後端已開始執行，可能仍會計入額度。');
        return;
      }
      if (!reviewerReady()) {
        showToast('AI 審查尚未開放或服務暫不可用');
        return;
      }

      if (!els.article) { showToast('請貼上完整文章（至少 80 字）'); return; }
      var payload = {
        title: els.title ? els.title.value.trim() : '',
        article: els.article ? els.article.value.trim() : '',
        audience_hint: els.audience ? els.audience.value.trim() : '',
        authority_evidence: els.evidence ? els.evidence.value.trim() : '',
        channel: els.channel ? els.channel.value : ''
      };

      if (payload.article.length < MIN_ARTICLE_CHARS) {
        showToast('請貼上完整文章（至少 80 字）');
        if (els.article) els.article.focus();
        return;
      }

      if (!reviewEndpoint) {
        showError('AI 審查接口未設定。');
        return;
      }

      var epoch = sessionEpoch;
      var sequence = ++reviewSequence;
      resetResult();
      reviewedArticle = els.article.value;
      reviewController = typeof AbortController === 'function' ? new AbortController() : null;
      if (els.loading) els.loading.hidden = false;
      var resultPanel = q(root, '[data-news-ai-result-panel]');
      if (resultPanel) resultPanel.hidden = false;
      root.dataset.newsAiHasResult = 'true';
      if (els.placeholder) els.placeholder.hidden = true;
      if (els.runButton) {
        els.runButton.disabled = false;
        els.runButton.textContent = '取消審查';
      }

      nativeRequest(reviewEndpoint, {
        signal: reviewController ? reviewController.signal : undefined,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify(payload)
      })
        .then(function (response) {
          return safeJson(response).then(function (data) {
            if (!response.ok) {
              var message = response.status === 403
                ? '目前帳戶尚未獲得 News AI 審查權限；你可繼續編輯文章，但不能執行受保護操作。'
                : response.status === 401
                  ? '登入已失效，請透過原有登入入口重新登入；文章會保留在本頁直至重新載入。'
                  : data && data.error && data.failureMessage(error)
                    ? data.failureMessage(error)
                    : 'Agent HTTP ' + response.status;
              /** @type {import('../types/news-ai').RuntimeError} */
              var error = new Error(message);
              error.runId = data && data.run_id ? data.run_id : '';
              throw error;
            }
            if (epoch === sessionEpoch && sequence === reviewSequence) render(data);
          });
        })
        .catch(function (error) {
          if (epoch !== sessionEpoch || sequence !== reviewSequence || error && (error.name === 'AbortError' || error.code === 'stale_session')) return;
          showError(error.message, error.runId || '');
        })
        .finally(function () {
          if (epoch !== sessionEpoch || sequence !== reviewSequence) return;
          reviewController = null;
          if (els.runButton) {
            els.runButton.disabled = !reviewerReady();
            els.runButton.textContent = reviewerReady() ? '執行 AI 文章審查' : 'AI 審查尚未開放或服務暫不可用';
          }
        });
    }


  /**
   * @param {string} url
   */
  function openNewsSource(url) {
      try {
        var parsed = new URL(url);
        var host = parsed.hostname.toLowerCase().replace(/\.$/, '');
        if (parsed.protocol !== 'https:' || parsed.username || parsed.password ||
            !host.includes('.') || host === 'localhost' ||
            /^(?:0|10|127|192\.168)\./.test(host) ||
            /^172\.(?:1[6-9]|2\d|3[01])\./.test(host) ||
            /^(?:\[?::1\]?|\[?fc|\[?fd|\[?fe80)/i.test(host)) return '';
        return parsed.href;
      } catch (_) { return ''; }
    }


    /**
     * @param {import('../types/news-ai').NewsItem[]} items
     */
    function renderTrends(items) {
      const results = els.writerResults;
      const sources = els.writerSources;
      if (!results || !sources) return;
      results.textContent = '';
      if (!items.length) { writerState('未找到近期新聞線索。請調整關鍵字或自行提供來源。'); return; }
      var paragraph = document.createElement('p');
      paragraph.textContent = '以下為新聞搜尋結果，尚未核實。請閱讀原文、查證內容，再加入來源。';
      results.appendChild(paragraph);
      items.forEach(function (item) {
        var url = openNewsSource(item.url);
        if (!url) return;
        var card = document.createElement('div');
        card.className = 'glg-article-reviewer__writer-result';
        var content = document.createElement('div');
        var link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.textContent = String(item.title || '查看來源');
        var date = document.createElement('small');
        date.textContent = String(item.published_at || '') + ' · 未核實';
        content.appendChild(link);
        content.appendChild(date);
        var select = document.createElement('button');
        select.type = 'button';
        select.textContent = '加入來源';
        listen(root, select, 'click', function () {
          var lines = (sources.value || '').split('\n').map(function (x) { return x.trim(); }).filter(Boolean);
          if (lines.indexOf(url) < 0) lines.push(url);
          sources.value = lines.slice(0, 6).join('\n');
          cancelWriter();
          writerState('已加入來源網址。請閱讀原文並在下方填寫核實的新聞證據。');
        });
        card.appendChild(content);
        card.appendChild(select);
        results.appendChild(card);
      });
    }

    function findTrends() {
      var keyword = els.writerTopic ? els.writerTopic.value.trim() : '';
      if (!researchReady || !publicSearchEndpoint || keyword.length < 2) {
        writerState('公開新聞搜尋即將推出；須完成 Public API、限流及來源驗證後開放。');
        return;
      }
      var url = new URL(publicSearchEndpoint);
      url.searchParams.set('q', keyword);
      var epoch = sessionEpoch;
      var sequence = ++searchSequence;
      if (publicSearchController) publicSearchController.abort();
      publicSearchController = new AbortController();
      url.searchParams.set('locale', 'zh-HK');
      writerState('正在搜尋新聞來源…');
      if (els.writerSearch) els.writerSearch.disabled = true;
      fetch(url.toString(), { method: 'GET', credentials: 'omit', redirect: 'error', headers: { Accept: 'application/json' }, cache: 'no-store', signal: publicSearchController.signal })
        .then(function (res) {
          return safeJson(res).then(function (body) {
            if (!res.ok || body.ok !== true || !Array.isArray(body.items)) throw Error('新聞搜尋暫不可用');
            if (epoch !== sessionEpoch || sequence !== searchSequence) return;
            renderTrends(body.items);
            writerState('已找到 ' + body.items.length + ' 條未核實新聞線索。');
          });
        })
        .catch(function (error) { if (epoch !== sessionEpoch || sequence !== searchSequence || error.name === 'AbortError' || error.code === 'stale_session') return; writerState(error.message || '新聞搜尋失敗'); })
        .finally(function () { if (epoch === sessionEpoch && sequence === searchSequence) { publicSearchController = null; if (els.writerSearch) els.writerSearch.disabled = !researchReady; } });
    }
  function generateDraft() {
      var topic = els.writerTopic ? els.writerTopic.value.trim() : '';
      var evidence = els.writerEvidence ? els.writerEvidence.value.trim() : '';
      var urls = els.writerSources ? els.writerSources.value.split('\n').map(function (s) { return s.trim(); }).filter(Boolean) : [];
      var valid = urls.length >= 1 && urls.length <= 6 && urls.every(openNewsSource);
      if (!writerReady()) { writerState('AI Writer 尚未開放或服務暫不可用。'); return; }
      if (!draftEndpoint || topic.length < 5 || evidence.length < 80 || !valid) {
        writerState('請提供至少 5 字的主題、1–6 個 HTTPS 原文網址，以及至少 80 字已核實證據。');
        return;
      }
      if (writerController) return;
      var epoch = sessionEpoch;
      var sequence = ++writerSequence;
      writerController = typeof AbortController === 'function' ? new AbortController() : null;
      if (els.writerGenerate) els.writerGenerate.disabled = true;
      writerState('AI 正在根據已核實內容生成文章草稿…');
      if (els.writerProof) els.writerProof.hidden = true;
      nativeRequest(draftEndpoint, {
        signal: writerController ? writerController.signal : undefined,
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          topic: topic, evidence: evidence,
          sources: urls.map(function (url) { return { url: url }; }),
          audience: els.audience ? els.audience.value.trim() : '',
          angle: 'GLG 測繪／GIS／規劃／城市空間智能專業分析，不推銷不杜撰'
        })
      })
        .then(function (res) {
          return safeJson(res).then(function (data) {
            if (epoch !== sessionEpoch || sequence !== writerSequence) return;
            if (!res.ok) {
              var message = res.status === 401 ? '登入已失效，請透過原有登入入口重新登入；原稿已保留。'
                : res.status === 403 ? '目前帳戶尚未獲得 News AI Writer 權限。'
                : res.status === 429 ? '已達額度或請求頻率上限，請稍後再試。'
                : res.status === 503 ? 'AI Writer 服務暫不可用；輸入已保留。'
                : 'Writer HTTP ' + res.status;
              throw new Error(message);
            }
            if (!data || data.runtime?.verified_ai_run !== true || typeof data.interaction_id !== 'string' || !data.interaction_id ||
                !data.draft || typeof data.draft.article !== 'string' || !data.draft.article.trim() ||
                typeof data.draft.title !== 'string' || !data.draft.title.trim() ||
                typeof data.draft.excerpt !== 'string' || !Array.isArray(data.draft.claims_to_verify) ||
                !data.draft.claims_to_verify.every(function (/** @type {unknown} */ claim) { return typeof claim === 'string'; })) {
              throw Error('無法核實真實 AI Writer 執行結果');
            }
            if (epoch !== sessionEpoch || sequence !== writerSequence) return;
            if (els.title) els.title.value = data.draft.title;
            if (els.article) els.article.value = data.draft.article;
            syncRteFromArticle();
            updateWordCount();
            resetResult();
            if (els.writerProof) {
              els.writerProof.hidden = false;
              els.writerProof.textContent = '草稿待審 · Run ' + data.run_id + ' · Interaction ' + data.interaction_id
                + ' · 仍需核實：' + (data.draft.claims_to_verify || []).join('；');
            }
            writerState('已建立文章草稿，尚未發佈。請核實來源，再使用 AI Reviewer 審查。');
            if (els.article) els.article.focus();
          });
        })
        .catch(function (error) {
          if (epoch !== sessionEpoch || sequence !== writerSequence || error && error.code === 'stale_session') return;
          if (error && error.name === 'AbortError') {
            writerState('已取消草稿請求；如後端已開始執行，可能仍會計入額度。');
            return;
          }
          writerState(error && error.message || 'Writer 失敗；沒有生成替代稿');
        })
        .finally(function () { if (epoch === sessionEpoch && sequence === writerSequence) { writerController = null; if (els.writerGenerate) els.writerGenerate.disabled = !writerReady(); } });
    }

    function loadExample() {
      cancelWriter();
      invalidateReview();
      if (els.title) els.title.value = '城市更新為何容易陷入「假進度」';
      if (els.audience) {
        els.audience.value = '鎮街主管、村集體負責人、城市更新項目負責人';
      }
      if (els.evidence) {
        els.evidence.value = '廣量在測繪、規劃、評估及城市更新方面的實務經驗（示例，發佈前須核實）';
      }
      if (els.article) {
        els.article.value =
          '随着城市更新进入存量阶段，很多项目表面上一直在推进：开会了、方案做了、效果图出了、产业定位也有了，甚至专家评审都开过。但半年后再问，哪些地能动、权属怎么解决、资金怎么平衡、下一步谁来办，答案仍然模糊。\n\n' +
          '这类项目最危险的，不是“进度慢”，而是把活动量误认为推进量。对项目负责人来说，一个更实用的判断是：真正的推进，不看做了多少动作，而看不确定性减少了多少。\n\n' +
          '我们在多个城乡更新、测绘、规划与评估项用途实操中，通常会先检查四件事：第一，现状底数是否已经锁定；第二，权属和利益主体是否清楚；第三，实施路径与报批条件是否形成；第四，资金与价值平衡是否能够闭合。\n\n' +
          '如果这四件事仍然没有答案，那么再多会议、方案和效果图，也只能说明项目“很忙”，不能说明项目已经进入实质推进。\n\n' +
          '这也意味着，片区统筹的核心不是把方案做得更大，而是把关键不确定性逐项变成可执行条件。对管理层而言，可以直接用一句话检查项目：这一个月，我们到底减少了哪一项关键不确定性？\n\n' +
          '广量更关注的不是替项目增加一份材料，而是通过测绘底数、规划研判、评估测算、更新实施与数字化工具，把“还不知道”逐步变成“可以决策、可以报批、可以实施”。';
      }

      syncRteFromArticle();
      updateWordCount();
      showToast('已載入測試範例');
    }

    function buildCopyText() {
      if (!latestResult) return '';

      return [
        (latestResult.decision === 'PASS' ? '優秀' : latestResult.decision === 'BLOCK' ? '不合格' : '普通') + ' · ' + text(latestResult.score || 0) + '/100',
        latestResult.summary || '',
        '',
        (latestResult.checks || []).map(function (check) {
          return [
            '0' + check.id,
            check.status === 'PASS' ? '優秀' : check.status === 'BLOCK' ? '不合格' : '普通',
            check.reason || '',
            check.fix ? '最小修訂：' + check.fix : ''
          ].join(' · ');
        }).join('\n'),
        '',
        '分享重點：' + (latestResult.share_line || '—'),
        '會議／簡報重點：' + (latestResult.meeting_line || '—'),
        '優先修訂：' + (latestResult.priority_fix || '—')
      ].join('\n');
    }

    if (els.writerSearch) listen(root, els.writerSearch, 'click', findTrends);
    if (els.writerGenerate) listen(root, els.writerGenerate, 'click', generateDraft);
    if (els.article) listen(root, els.article, 'input', function () { cancelWriter(); syncRteFromArticle(); updateWordCount(); invalidateReview(); });
    if (els.article) listen(root, els.article, 'paste', function () { window.setTimeout(function () { if (root.isConnected) { syncRteFromArticle(); updateWordCount(); invalidateReview(); } }, 0); });
    [els.title, els.evidence, els.audience, els.channel].forEach(function (field) {
      if (field) {
        listen(root, field, 'input', function () { cancelWriter(); invalidateReview(); });
        listen(root, field, 'change', invalidateReview);
      }
    });
    [els.writerTopic, els.writerSources, els.writerEvidence].forEach(function (field) {
      if (field) listen(root, field, 'input', cancelWriter);
    });
    if (els.runButton) listen(root, els.runButton, 'click', runReview);
    if (els.exampleButton) listen(root, els.exampleButton, 'click', loadExample);

    if (els.copyAllButton) {
      listen(root, els.copyAllButton, 'click', function () {
        var value = buildCopyText();
        if (!value) {
          showToast('目前沒有可驗證的 AI 審查結果');
          return;
        }
        copyToClipboard(value)
          .then(function () { showToast('已複製完整審查結果'); })
          .catch(function () { showToast('瀏覽器未允許複製'); });
      });
    }

    qa(root, '[data-reviewer-copy]').forEach(function (button) {
      listen(root, button, 'click', function () {
        var type = button.dataset.reviewerCopy;
        var value =
          type === 'share' && els.shareLine ? els.shareLine.textContent :
          type === 'meeting' && els.meetingLine ? els.meetingLine.textContent :
          '';

        if (!value) return;

        copyToClipboard(value)
          .then(function () { showToast('已複製'); })
          .catch(function () { showToast('瀏覽器未允許複製'); });
      });
    });

    function clearPrivateEditor() {
      // Native Session owns identity. This tag only fences this editor's data;
      // never carry private manual edits, source material or results to a new owner.
      [els.title, els.article, els.audience, els.evidence,
        els.writerTopic, els.writerSources, els.writerEvidence].forEach(function (field) {
        if (field) field.value = '';
      });
      if (els.channel) els.channel.selectedIndex = 0;
      [els.errorMessage, els.errorRun, els.decision, els.decisionTitle, els.score,
        els.summaryCopy, els.runId, els.interactionId, els.usage, els.shareLine,
        els.meetingLine, els.priorityFix, els.authority, els.missing].forEach(function (node) {
        if (node) node.textContent = '';
      });
      if (els.decision) delete els.decision.dataset.decision;
      clearManualDraft();
      clearMarkdownScratchpads.forEach(function (clear) { clear(); });
      clearAttachments.forEach(function (clear) { clear(); });
      syncRteFromArticle();
      window.clearTimeout(toastTimer);
      if (els.toast) { els.toast.textContent = ''; els.toast.dataset.visible = 'false'; }
      updateWordCount();
    }

    listen(root, document, 'littleg:web-session-change', function () {
      sessionEpoch++;
      healthSequence++;
      searchSequence++;
      writerSequence++;
      reviewSequence++;
      stopPendingRequests();
      if (reviewController) { reviewController.abort(); reviewController = null; }
      resetResult();
      reviewedArticle = null;
      var nextOwner = draftOwner();
      var identityChanged = nextOwner !== editorOwner;
      editorOwner = nextOwner;
      if (identityChanged) {
        clearPrivateEditor();
        draftMessage('身份已變更；上一身份的草稿及私人結果已清除。');
      }
      writerState(identityChanged ? '身份已變更；請重新輸入內容並確認功能權限。' : '正在重新確認登入及功能權限。');
      setRuntime('offline');
      if (els.writerResults) els.writerResults.textContent = '';
      if (els.writerProof) { els.writerProof.hidden = true; els.writerProof.textContent = ''; }
      const session = window.LittleGWebSession;
      var state = session && session.getState && session.getState();
      if (state && state.status === 'authenticated') checkHealth();
    });
    updateWordCount();
    checkHealth();
    const entry = mounted.get(root);
    if (!entry) return;
    entry.dispose = function () {
      sessionEpoch++;
      stopPendingRequests();
      window.clearTimeout(toastTimer);
      resetResult();
      clearPrivateEditor();
      if (els.writerResults) els.writerResults.textContent = '';
      if (els.writerProof) els.writerProof.textContent = '';
      entry.controller.abort();
      mounted.delete(root);
      delete root.dataset.reviewerInitialized;
      qa(root, '[data-feature]').forEach(function (node) { delete node.dataset.mdInitialized; delete node.dataset.uploaderInitialized; });
    };
  }


  /**
   * @param {ParentNode | EventTarget | null} [scope]
   */
  function initAll(scope) {
    mounted.forEach(function (entry, node) { if (!node.isConnected && entry.dispose) entry.dispose(); });
    const target = scope && 'querySelectorAll' in scope ? scope : document;
    if (target instanceof HTMLElement && target.matches(ROOT_SELECTOR)) init(target);
    qa(target, ROOT_SELECTOR).forEach(init);
  }

  window[RUNTIME_KEY] = { boot: initAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); }, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    mounted.forEach(function (entry, node) {
      if (event.target === node || (event.target instanceof Node && event.target.contains(node))) entry.dispose?.();
    });
  });
})(/** @type {import('../types/news-ai').NewsWindow} */ (window));
